import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Layers, AlertTriangle, Eye, ChevronLeft, ChevronRight, Users, Filter, ExternalLink, RefreshCw, Search, CheckCircle2, AlertCircle, ArrowLeft, Clock, ArrowUpDown, ArrowUp, ArrowDown, X, Download, FileSpreadsheet, FileText, ChevronDown, ChevronUp, DollarSign, CalendarDays, BookOpen, Zap, Loader2, ClipboardCheck } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ZohoCustomerLink, ZohoInvoiceLink, ZohoCrmLeadLink, EnrollmentNoCrmLink } from '../../components/ZohoLinks';
import { BookmarkInline, BookmarkMapProvider, useBookmarkMap, bookmarkRowClass } from '../../components/BookmarkButton';

function hasActiveFilters(filters, search) {
  return search || filters.batch_statuses.length > 0 || filters.course_ids.length > 0 ||
    filters.trainer_names.length > 0 || filters.centre_ids.length > 0 ||
    filters.batch_type || filters.batch_number_min || filters.batch_number_max ||
    filters.delayed_only || filters.start_date_preset;
}

function SyllabusLinkCell({ batchId, link, syllabi, onChange }) {
  const value = link?.course_id || '__none__';
  return (
    <Select value={value} onValueChange={(v) => onChange(batchId, v)}>
      <SelectTrigger className={`h-7 text-[10px] max-w-[170px] rounded-sm ${link ? (link.auto_linked ? 'border-violet-200 bg-violet-50/40' : 'border-emerald-200 bg-emerald-50/40') : 'border-slate-200 text-slate-400'}`} data-testid={`syllabus-link-${batchId}`}>
        {link ? (
          <span className="flex items-center gap-1 truncate">
            <BookOpen className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{link.course_name}</span>
            {link.auto_linked && <Zap className="w-2.5 h-2.5 text-violet-400 flex-shrink-0" />}
          </span>
        ) : (
          <span className="text-slate-400">Link syllabus</span>
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__" className="text-xs text-slate-500">— Unlink —</SelectItem>
        {syllabi.map(s => (
          <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── Inline Lab assignment dropdown ───────────────────────
// Editable for Running / Upcoming batches; read-only otherwise (Completed /
// On-Hold / Cancelled batches don't need a lab). Options are all labs from
// the master, grouped by location so it's easy to pick the right one.
function LabAssignCell({ batch, labs, onAssign }) {
  const status = batch.batch_status || batch.status || '';
  const editable = ['Running', 'Upcoming'].includes(status);
  const value = batch.lab_id || '__none__';
  const currentLab = labs.find((l) => l.id === batch.lab_id);
  const displayName = currentLab ? `${currentLab.lab_number} · ${currentLab.location_name}` : (batch.lab_name || null);

  if (!editable) {
    return (
      <span className={`text-[10px] ${displayName ? 'text-slate-600' : 'text-slate-300 italic'}`}>
        {displayName || 'n/a'}
      </span>
    );
  }

  const grouped = labs.reduce((acc, l) => {
    const key = l.location_name || 'Other';
    (acc[key] = acc[key] || []).push(l);
    return acc;
  }, {});

  return (
    <Select value={value} onValueChange={(v) => onAssign(batch.id, v)}>
      <SelectTrigger
        className={`h-7 text-[10px] max-w-[160px] rounded-sm ${batch.lab_id ? 'border-emerald-200 bg-emerald-50/40 text-emerald-800' : 'border-rose-200 bg-rose-50/40 text-rose-700'}`}
        data-testid={`batch-lab-select-${batch.id}`}
      >
        {batch.lab_id ? (
          <span className="truncate">{displayName}</span>
        ) : (
          <span>Unassigned</span>
        )}
      </SelectTrigger>
      <SelectContent className="max-h-[320px]">
        <SelectItem value="__none__" className="text-xs text-slate-500">— Clear (unassigned) —</SelectItem>
        {Object.keys(grouped).sort().map((loc) => (
          <div key={loc}>
            <p className="px-2 pt-1.5 pb-0.5 text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{loc}</p>
            {grouped[loc].map((l) => (
              <SelectItem key={l.id} value={l.id} className="text-xs">
                {l.lab_number}{l.capacity ? ` · cap ${l.capacity}` : ''}
              </SelectItem>
            ))}
          </div>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── Inline Lab assignment card for the Batch Detail Overview ───
// Same functionality as LabAssignCell (used in the batches table) but
// styled as an InfoCard so it drops cleanly into the Overview grid.
// Editable only for Running / Upcoming batches — matches the table rule.
function LabInfoCard({ batch, labs, onAssign, saving }) {
  const status = batch.batch_status || batch.status || '';
  const editable = ['Running', 'Upcoming'].includes(status);
  const currentLab = labs.find((l) => l.id === batch.lab_id);
  const displayName = currentLab
    ? `${currentLab.lab_number} · ${currentLab.location_name}${currentLab.capacity ? ` · cap ${currentLab.capacity}` : ''}`
    : (batch.lab_name || null);

  // Read-only path (Completed / On-Hold / Cancelled)
  if (!editable) {
    return (
      <Card className="p-4 border" data-testid="batch-detail-lab-card">
        <p className="text-[10px] text-slate-400 uppercase">Lab</p>
        <p className={`mt-1 text-sm ${displayName ? 'text-slate-700' : 'text-slate-300 italic'}`}>
          {displayName || 'n/a'}
        </p>
      </Card>
    );
  }

  const grouped = labs.reduce((acc, l) => {
    const key = l.location_name || 'Other';
    (acc[key] = acc[key] || []).push(l);
    return acc;
  }, {});
  const isUnassigned = !batch.lab_id;

  return (
    <Card
      className={`p-4 border ${isUnassigned ? 'border-rose-200 bg-rose-50/40' : 'border-emerald-200 bg-emerald-50/40'}`}
      data-testid="batch-detail-lab-card"
    >
      <div className="flex items-center gap-1.5">
        <p className="text-[10px] text-slate-400 uppercase">Lab</p>
        {isUnassigned && <span className="text-[9px] font-semibold text-rose-600 uppercase">Unassigned</span>}
        {saving && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
      </div>
      <Select value={batch.lab_id || '__none__'} onValueChange={onAssign} disabled={saving}>
        <SelectTrigger
          className={`mt-1.5 h-8 text-xs ${isUnassigned ? 'border-rose-300 text-rose-700' : 'border-emerald-300 text-emerald-800 font-medium'}`}
          data-testid="batch-detail-lab-select"
        >
          {displayName ? <span className="truncate">{displayName}</span> : <span>Pick a lab…</span>}
        </SelectTrigger>
        <SelectContent className="max-h-[320px]">
          <SelectItem value="__none__" className="text-xs text-slate-500">— Clear (unassigned) —</SelectItem>
          {Object.keys(grouped).sort().map((loc) => (
            <div key={loc}>
              <p className="px-2 pt-1.5 pb-0.5 text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{loc}</p>
              {grouped[loc].map((l) => (
                <SelectItem key={l.id} value={l.id} className="text-xs">
                  {l.lab_number}{l.capacity ? ` · cap ${l.capacity}` : ''}
                </SelectItem>
              ))}
            </div>
          ))}
        </SelectContent>
      </Select>
    </Card>
  );
}




function getDateRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const fmt = (d) => d.toISOString().slice(0, 10);
  switch (preset) {
    case 'this_month': return { from: fmt(new Date(y, m, 1)), to: fmt(new Date(y, m + 1, 0)) };
    case 'prev_month': return { from: fmt(new Date(y, m - 1, 1)), to: fmt(new Date(y, m, 0)) };
    case 'this_quarter': { const qm = Math.floor(m / 3) * 3; return { from: fmt(new Date(y, qm, 1)), to: fmt(new Date(y, qm + 3, 0)) }; }
    case 'prev_quarter': { const qm = Math.floor(m / 3) * 3 - 3; const qy = qm < 0 ? y - 1 : y; const aqm = ((qm % 12) + 12) % 12; return { from: fmt(new Date(qy, aqm, 1)), to: fmt(new Date(qy, aqm + 3, 0)) }; }
    default: return { from: '', to: '' };
  }
}

function StartDateFilter({ filters, setFilters, setPage }) {
  const [showCustom, setShowCustom] = useState(false);

  const handlePreset = (v) => {
    if (v === 'all') {
      setFilters(p => ({ ...p, start_date_preset: '', start_date_from: '', start_date_to: '' }));
      setShowCustom(false);
    } else if (v === 'custom') {
      setFilters(p => ({ ...p, start_date_preset: 'custom' }));
      setShowCustom(true);
    } else {
      const { from, to } = getDateRange(v);
      setFilters(p => ({ ...p, start_date_preset: v, start_date_from: from, start_date_to: to }));
      setShowCustom(false);
    }
    setPage(1);
  };

  const presetLabel = {
    this_month: 'This Month',
    prev_month: 'Previous Month',
    this_quarter: 'This Quarter',
    prev_quarter: 'Previous Quarter',
    custom: 'Custom',
  };

  return (
    <Select value={filters.start_date_preset || 'all'} onValueChange={handlePreset}>
      <SelectTrigger className="w-36 h-9 text-xs rounded-sm" data-testid="start-date-filter">
        <CalendarDays className="w-3 h-3 mr-1 text-slate-400" />
        <SelectValue placeholder="Start Date" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Dates</SelectItem>
        <SelectItem value="this_month">This Month</SelectItem>
        <SelectItem value="prev_month">Previous Month</SelectItem>
        <SelectItem value="this_quarter">This Quarter</SelectItem>
        <SelectItem value="prev_quarter">Previous Quarter</SelectItem>
        <SelectItem value="custom">Custom Range</SelectItem>
      </SelectContent>
    </Select>
  );
}

const bsColors = {
  Running: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Completed: 'bg-blue-50 text-blue-700 border-blue-200',
  'On Hold': 'bg-amber-50 text-amber-700 border-amber-200',
  Upcoming: 'bg-violet-50 text-violet-700 border-violet-200',
  Open: 'bg-slate-100 text-slate-600 border-slate-200',
};

function SortHead({ label, field, sortBy, sortOrder, onSort, align }) {
  const active = sortBy === field;
  const handleClick = () => {
    if (active) onSort(field, sortOrder === 'asc' ? 'desc' : 'asc');
    else onSort(field, 'asc');
  };
  return (
    <TableHead className={`text-[10px] uppercase cursor-pointer hover:bg-slate-50 select-none ${align === 'right' ? 'text-right' : ''}`} onClick={handleClick}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active ? (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 text-violet-600" /> : <ArrowDown className="w-3 h-3 text-violet-600" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
      </span>
    </TableHead>
  );
}

function MultiSelect({ options, selected, onChange, placeholder, testId, searchable = false }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const toggle = (val) => {
    const s = new Set(selected);
    if (s.has(val)) s.delete(val); else s.add(val);
    onChange(Array.from(s));
  };
  const label = selected.length === 0 ? placeholder : `${selected.length} selected`;
  const filtered = searchable && searchTerm ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase())) : options;
  return (
    <div className="relative" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 h-9 px-3 border rounded-sm text-xs text-slate-600 bg-white hover:bg-slate-50 min-w-[120px]">
        <Filter className="w-3 h-3 text-slate-400" />{label}
        {selected.length > 0 && <X className="w-3 h-3 ml-auto text-slate-400 hover:text-red-500" onClick={e => { e.stopPropagation(); onChange([]); }} />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearchTerm(''); }} />
          <div className="absolute z-50 w-56 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
            {searchable && (
              <div className="p-1.5 border-b">
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..." className="w-full h-7 px-2 text-xs border rounded-sm outline-none focus:border-violet-300" autoFocus data-testid={`${testId}-search`} />
              </div>
            )}
            <div className="overflow-y-auto max-h-52">
              {filtered.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">No matches</p>
              ) : filtered.map(o => (
                <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-violet-50 cursor-pointer text-xs">
                  <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} className="rounded border-slate-300" />
                  {o.label}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function BatchManagementPage() {
  const [batches, setBatches] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [viewBatchId, setViewBatchId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link support: /training/batches?focus=<batch_id>
  // Notifications ("Batch reconciliation issue…") and the Trainer Lab
  // "Unallocated batches" card link here so a click lands directly on the
  // specific batch, not the paginated list.
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (!focusId) return;
    setViewBatchId(focusId);
    // Strip the param so refresh doesn't re-open the same batch.
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [deleteBatch, setDeleteBatch] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ batch_statuses: [], course_ids: [], trainer_names: [], centre_ids: [], batch_type: '', batch_number_min: '', batch_number_max: '', delayed_only: false, start_date_preset: '', start_date_from: '', start_date_to: '' });
  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [centres, setCentres] = useState([]);
  const [labs, setLabs] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [mapping, setMapping] = useState(false);
  const [syllabi, setSyllabi] = useState([]);
  const [batchLinks, setBatchLinks] = useState({});
  const [autoLinking, setAutoLinking] = useState(false);
  const pageSize = 20;

  const fetchBatches = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (filters.batch_statuses.length) params.append('batch_statuses', filters.batch_statuses.join(','));
    if (filters.course_ids.length) params.append('course_ids', filters.course_ids.join(','));
    if (filters.trainer_names.length) params.append('trainer_names', filters.trainer_names.join(','));
    if (filters.centre_ids.length) params.append('centre_ids', filters.centre_ids.join(','));
    if (filters.batch_type) params.append('batch_type', filters.batch_type);
    if (filters.batch_number_min) params.append('batch_number_min', filters.batch_number_min);
    if (filters.batch_number_max) params.append('batch_number_max', filters.batch_number_max);
    if (search) params.append('search', search);
    if (filters.delayed_only) params.append('delayed_only', 'true');
    if (filters.start_date_from) params.append('start_date_from', filters.start_date_from);
    if (filters.start_date_to) params.append('start_date_to', filters.start_date_to);
    if (sortBy) { params.append('sort_by', sortBy); params.append('sort_order', sortOrder); }
    api.get(`/training/batches?${params}`).then(r => { setBatches(r.data.batches || []); setTotal(r.data.total || 0); }).catch(() => {}).finally(() => setLoading(false));
  }, [page, filters, search, sortBy, sortOrder]);

  const fetchSyncStatus = useCallback(() => {
    api.get('/training/zoho/crm-sync-status').then(r => {
      setSyncStatus(r.data.last_sync);
      if (r.data.is_running && !syncing) setSyncing(true);
    }).catch(() => {});
  }, [syncing]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);
  useEffect(() => {
    Promise.all([
      api.get('/training/courses'),
      api.get('/training/trainers'),
      api.get('/training/centres'),
      api.get('/courses/syllabus'),
      api.get('/courses/batch-links'),
      api.get('/venue/admin/labs').catch(() => ({ data: { labs: [] } })),
    ]).then(([cR, tR, ceR, sR, blR, lR]) => {
      setCourses(cR.data.courses || []);
      setTrainers(tR.data.trainers || []);
      setCentres(ceR.data.centres || []);
      setSyllabi(sR.data.syllabi || []);
      setBatchLinks(blR.data.links || {});
      setLabs(lR.data.labs || []);
    });
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  const refreshBatchLinks = useCallback(() => {
    api.get('/courses/batch-links').then(r => setBatchLinks(r.data.links || {})).catch(() => {});
  }, []);

  const handleAssignLab = async (batchId, labId) => {
    try {
      // Empty string / "__none__" clears the lab (batch back to unallocated)
      const value = (!labId || labId === '__none__') ? null : labId;
      await api.put(`/training/batches/${batchId}`, { lab_id: value });
      // Optimistic update in-place
      setBatches((prev) => prev.map((b) => {
        if (b.id !== batchId) return b;
        if (value === null) return { ...b, lab_id: null, lab_name: null };
        const lab = labs.find((l) => l.id === value);
        return { ...b, lab_id: value, lab_name: lab ? `${lab.lab_number} · ${lab.location_name}` : null };
      }));
      toast.success(value ? 'Lab assigned' : 'Lab cleared');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update lab');
    }
  };

  const handleLinkSyllabus = async (batchId, courseId) => {
    try {
      if (!courseId || courseId === '__none__') {
        await api.delete(`/courses/batch-link/${batchId}`);
        setBatchLinks(p => { const n = { ...p }; delete n[batchId]; return n; });
        toast.success('Syllabus unlinked');
      } else {
        await api.post('/courses/batch-link', { batch_id: batchId, course_id: courseId });
        const s = syllabi.find(x => x.id === courseId);
        setBatchLinks(p => ({ ...p, [batchId]: { course_id: courseId, course_name: s?.name || '', auto_linked: false } }));
        toast.success(`Linked to ${s?.name || 'syllabus'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link');
    }
  };

  const handleAutoLinkAll = async () => {
    if (!window.confirm('Auto-link all unlinked batches using syllabus keywords? Manual links are preserved.')) return;
    setAutoLinking(true);
    try {
      const r = await api.post('/courses/auto-link-all', {});
      toast.success(`Auto-link: ${r.data.linked} linked, ${r.data.skipped} kept, ${r.data.unmatched} no match`);
      refreshBatchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-link failed');
    } finally {
      setAutoLinking(false);
    }
  };

  const handleMapAll = async () => {
    setMapping(true);
    toast.info('Starting student mapping for all batches...');
    try {
      const r = await api.post('/training/zoho/map-all-students');
      if (r.data.status === 'already_running') {
        toast.warning('Student mapping already in progress');
      } else {
        toast.success('Student mapping started. Will refresh when complete.');
        const poll = setInterval(async () => {
          try {
            const sr = await api.get('/training/zoho/map-students-status');
            if (!sr.data.is_running) {
              clearInterval(poll);
              setMapping(false);
              const last = sr.data.last_sync;
              if (last?.status === 'success') {
                toast.success(`Mapping complete: ${last.enrollments_created} enrollments across ${last.batches_processed} batches`);
              } else if (last?.status === 'failed') {
                toast.error(`Mapping failed: ${last.error || 'Unknown'}`);
              }
              fetchBatches();
            }
          } catch { /* ignore */ }
        }, 3000);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Mapping failed'); setMapping(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    toast.info('Starting CRM batch sync...');
    try {
      const r = await api.post('/training/zoho/crm-sync');
      if (r.data.status === 'already_running') {
        toast.warning('CRM sync already in progress');
      } else {
        toast.success('CRM sync started. Will refresh when complete.');
        const poll = setInterval(async () => {
          try {
            const sr = await api.get('/training/zoho/crm-sync-status');
            setSyncStatus(sr.data.last_sync);
            if (!sr.data.is_running) {
              clearInterval(poll);
              setSyncing(false);
              const last = sr.data.last_sync;
              if (last?.status === 'success') {
                toast.success(`Sync complete: ${last.total_batches} batches, ${last.enrollments_created} enrollments`);
              } else if (last?.status === 'failed') {
                toast.error(`Sync failed: ${last.error || 'Unknown'}`);
              }
              fetchBatches();
            }
          } catch { /* ignore */ }
        }, 3000);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); setSyncing(false); }
  };

  const [batchKpi, setBatchKpi] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchAllForExport = async () => {
    const params = new URLSearchParams({ page: 1, page_size: 10000 });
    if (filters.batch_statuses.length) params.append('batch_statuses', filters.batch_statuses.join(','));
    if (filters.course_ids.length) params.append('course_ids', filters.course_ids.join(','));
    if (filters.trainer_names.length) params.append('trainer_names', filters.trainer_names.join(','));
    if (filters.centre_ids.length) params.append('centre_ids', filters.centre_ids.join(','));
    if (filters.batch_type) params.append('batch_type', filters.batch_type);
    if (filters.batch_number_min) params.append('batch_number_min', filters.batch_number_min);
    if (filters.batch_number_max) params.append('batch_number_max', filters.batch_number_max);
    if (search) params.append('search', search);
    if (filters.delayed_only) params.append('delayed_only', 'true');
    if (filters.start_date_from) params.append('start_date_from', filters.start_date_from);
    if (filters.start_date_to) params.append('start_date_to', filters.start_date_to);
    if (sortBy) { params.append('sort_by', sortBy); params.append('sort_order', sortOrder); }
    const r = await api.get(`/training/batches?${params}`);
    return r.data.batches || [];
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const data = await fetchAllForExport();
      const rows = data.map((b, i) => ({
        'S.No': i + 1,
        'Batch #': b.batch_number || '-',
        'Batch Name': b.name || b.course_name || '',
        'Course': b.course_name || '',
        'Trainer': b.trainer_name || '-',
        'Centre': b.centre_name || '',
        'Timing': b.start_time && b.end_time ? `${b.start_time} - ${b.end_time}` : b.duration_raw || '-',
        'Start Date': b.start_date || '-',
        'End Date': b.end_date || '-',
        'Mode': b.training_mode || b.mode_name || '-',
        'Type': b.batch_type || '-',
        'Students': b.student_count || 0,
        'Status': b.batch_status || b.status || '',
        'Delayed': b.is_delayed ? `Yes (${b.delay_days}d)` : 'No',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Batches');
      XLSX.writeFile(wb, `Batch_Management_${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const data = await fetchAllForExport();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(14);
      doc.text('Batch Management Report', 14, 15);
      doc.setFontSize(8);
      doc.text(`Generated: ${new Date().toLocaleString('en-IN')} | Total: ${data.length} batches`, 14, 21);
      const rows = data.map((b, i) => [
        i + 1,
        b.batch_number || '-',
        (b.name || b.course_name || '').substring(0, 30),
        (b.course_name || '').substring(0, 20),
        (b.trainer_name || '-').substring(0, 20),
        (b.centre_name || '').substring(0, 15),
        b.start_date || '-',
        b.student_count || 0,
        b.batch_status || b.status || '',
        b.is_delayed ? `${b.delay_days}d` : '-',
      ]);
      autoTable(doc, {
        startY: 25,
        head: [['#', 'Batch #', 'Name', 'Course', 'Trainer', 'Centre', 'Start', 'Students', 'Status', 'Delay']],
        body: rows,
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [109, 40, 217], fontSize: 7 },
      });
      doc.save(`Batch_Management_${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success('PDF downloaded');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.batch_statuses.length) params.append('batch_statuses', filters.batch_statuses.join(','));
    if (filters.course_ids.length) params.append('course_ids', filters.course_ids.join(','));
    if (filters.trainer_names.length) params.append('trainer_names', filters.trainer_names.join(','));
    if (filters.centre_ids.length) params.append('centre_ids', filters.centre_ids.join(','));
    if (filters.batch_type) params.append('batch_type', filters.batch_type);
    if (filters.batch_number_min) params.append('batch_number_min', filters.batch_number_min);
    if (filters.batch_number_max) params.append('batch_number_max', filters.batch_number_max);
    if (search) params.append('search', search);
    if (filters.delayed_only) params.append('delayed_only', 'true');
    if (filters.start_date_from) params.append('start_date_from', filters.start_date_from);
    if (filters.start_date_to) params.append('start_date_to', filters.start_date_to);
    api.get(`/training/reports/kpi/batches?${params}`).then(r => setBatchKpi(r.data)).catch(() => {});
  }, [batches, filters, search]);

  if (viewBatchId) {
    return <BatchDetailView batchId={viewBatchId} onBack={() => { setViewBatchId(null); fetchBatches(); }} />;
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6" data-testid="batch-management-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Batch Management</h1>
          <p className="text-sm text-slate-400 mt-1">{total} batch{total !== 1 ? 'es' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <SyncBadge status={syncStatus} syncing={syncing} />
          <Button onClick={exportExcel} disabled={exporting} variant="outline" className="rounded-sm text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="export-excel-btn">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />Excel
          </Button>
          <Button onClick={exportPDF} disabled={exporting} variant="outline" className="rounded-sm text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid="export-pdf-btn">
            <FileText className="w-3.5 h-3.5 mr-1.5" />PDF
          </Button>
          <Button onClick={handleMapAll} disabled={mapping} variant="outline" className="rounded-sm text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="map-all-students-btn">
            <Users className={`w-3.5 h-3.5 mr-1.5 ${mapping ? 'animate-pulse' : ''}`} />
            {mapping ? 'Mapping...' : 'Map Students'}
          </Button>
          <Button onClick={handleAutoLinkAll} disabled={autoLinking} variant="outline" className="rounded-sm text-xs border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="auto-link-syllabi-btn" title="Auto-link syllabi to unlinked batches using keyword matching">
            <Zap className={`w-3.5 h-3.5 mr-1.5 ${autoLinking ? 'animate-pulse' : ''}`} />
            {autoLinking ? 'Linking...' : 'Auto-link Syllabi'}
          </Button>
          <Button onClick={handleSync} disabled={syncing} variant="outline" className="rounded-sm text-xs border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="crm-sync-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from CRM'}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {batchKpi && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="batch-kpi-cards">
          <Card className="border p-3 text-center"><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Total</p><p className="text-lg font-bold text-slate-800">{batchKpi.total_batches}</p></Card>
          {[
            { key: 'Running', val: batchKpi.running, text: 'text-emerald-600', activeBg: 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-200', hover: 'hover:border-emerald-300' },
            { key: 'Completed', val: batchKpi.completed, text: 'text-blue-600', activeBg: 'bg-blue-50 border-blue-400 ring-1 ring-blue-200', hover: 'hover:border-blue-300' },
            { key: 'Upcoming', val: batchKpi.upcoming, text: 'text-violet-600', activeBg: 'bg-violet-50 border-violet-400 ring-1 ring-violet-200', hover: 'hover:border-violet-300' },
            { key: 'On Hold', val: batchKpi.on_hold, text: 'text-amber-600', activeBg: 'bg-amber-50 border-amber-400 ring-1 ring-amber-200', hover: 'hover:border-amber-300' },
          ].map(s => {
            const active = filters.batch_statuses.includes(s.key);
            return (
              <Card key={s.key}
                className={`border p-3 text-center cursor-pointer transition-colors ${active ? s.activeBg : s.hover}`}
                onClick={() => {
                  setFilters(p => {
                    const current = p.batch_statuses;
                    const next = current.includes(s.key) ? current.filter(x => x !== s.key) : [...current, s.key];
                    return { ...p, batch_statuses: next };
                  });
                  setPage(1);
                }}
                data-testid={`kpi-${s.key.toLowerCase().replace(' ', '-')}`}
              >
                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{s.key}</p>
                <p className={`text-lg font-bold ${s.text}`}>{s.val}</p>
              </Card>
            );
          })}
          <Card
            className={`border p-3 text-center cursor-pointer transition-colors ${filters.delayed_only ? 'bg-red-50 border-red-400 ring-1 ring-red-200' : 'hover:border-red-300'}`}
            onClick={() => { setFilters(p => ({ ...p, delayed_only: !p.delayed_only })); setPage(1); }}
            data-testid="kpi-delayed"
          >
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Delayed</p>
            <p className="text-lg font-bold text-red-600">{batchKpi.delayed}</p>
          </Card>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search batch name, course, trainer, batch #..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9 text-xs rounded-sm" data-testid="batch-search" />
        </div>
        <MultiSelect
          options={[{value:'Running',label:'Running'},{value:'Completed',label:'Completed'},{value:'On Hold',label:'On Hold'},{value:'Upcoming',label:'Upcoming'},{value:'Cancelled',label:'Cancelled'}]}
          selected={filters.batch_statuses} onChange={v => { setFilters(p => ({...p, batch_statuses: v})); setPage(1); }}
          placeholder="Status" testId="filter-batch-status"
        />
        <MultiSelect
          options={courses.map(c => ({value: c.id, label: c.name}))}
          selected={filters.course_ids} onChange={v => { setFilters(p => ({...p, course_ids: v})); setPage(1); }}
          placeholder="Course" testId="filter-course" searchable
        />
        <MultiSelect
          options={trainers.map(t => ({value: t.name, label: t.name}))}
          selected={filters.trainer_names} onChange={v => { setFilters(p => ({...p, trainer_names: v})); setPage(1); }}
          placeholder="Trainer" testId="filter-trainer" searchable
        />
        <MultiSelect
          options={centres.map(c => ({value: c.id, label: c.name}))}
          selected={filters.centre_ids} onChange={v => { setFilters(p => ({...p, centre_ids: v})); setPage(1); }}
          placeholder="Centre" testId="filter-centre"
        />
        <Select value={filters.batch_type || 'all'} onValueChange={v => { setFilters(p => ({...p, batch_type: v === 'all' ? '' : v})); setPage(1); }}>
          <SelectTrigger className="w-28 h-9 text-xs rounded-sm" data-testid="filter-batch-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="weekday">Weekday</SelectItem><SelectItem value="weekend">Weekend</SelectItem></SelectContent>
        </Select>
        <StartDateFilter filters={filters} setFilters={setFilters} setPage={setPage} />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold text-slate-400 uppercase whitespace-nowrap">Batch #</span>
          <Input type="number" placeholder="From" value={filters.batch_number_min} onChange={e => { setFilters(p => ({...p, batch_number_min: e.target.value})); setPage(1); }} className="h-9 w-20 text-xs rounded-sm" data-testid="filter-bn-min" />
          <span className="text-xs text-slate-400">–</span>
          <Input type="number" placeholder="To" value={filters.batch_number_max} onChange={e => { setFilters(p => ({...p, batch_number_max: e.target.value})); setPage(1); }} className="h-9 w-20 text-xs rounded-sm" data-testid="filter-bn-max" />
        </div>
        {hasActiveFilters(filters, search) && (
          <Button variant="ghost" size="sm" className="h-9 px-3 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-sm" onClick={() => { setFilters({ batch_statuses: [], course_ids: [], trainer_names: [], centre_ids: [], batch_type: '', batch_number_min: '', batch_number_max: '', delayed_only: false, start_date_preset: '', start_date_from: '', start_date_to: '' }); setSearch(''); setSortBy(''); setSortOrder('desc'); setPage(1); }} data-testid="clear-all-filters">
            <X className="w-3.5 h-3.5 mr-1" />Clear Filters
          </Button>
        )}
      </div>

      {/* Custom date range row — only visible when Custom Range is selected */}
      {filters.start_date_preset === 'custom' && (
        <div className="flex items-center gap-2 px-1">
          <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs font-medium text-slate-500">Custom Date Range:</span>
          <Input type="date" value={filters.start_date_from} onChange={e => { setFilters(p => ({ ...p, start_date_from: e.target.value })); setPage(1); }} className="h-9 w-36 text-xs rounded-sm" data-testid="start-date-from" />
          <span className="text-xs text-slate-400">to</span>
          <Input type="date" value={filters.start_date_to} onChange={e => { setFilters(p => ({ ...p, start_date_to: e.target.value })); setPage(1); }} className="h-9 w-36 text-xs rounded-sm" data-testid="start-date-to" />
        </div>
      )}

      <Card className="border p-0 overflow-x-auto">
        {loading ? (
          <div className="h-48 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div>
        ) : batches.length === 0 ? (
          <div className="py-16 text-center">
            <Layers className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No batches found. Click "Sync from CRM" to import batches.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortHead label="Batch #" field="batch_number" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} />
                <SortHead label="Batch Name" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} />
                <SortHead label="Course" field="course_name" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} />
                <SortHead label="Trainer" field="trainer_name" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} />
                <TableHead className="text-[10px] uppercase">Centre</TableHead>
                <TableHead className="text-[10px] uppercase">Lab</TableHead>
                <TableHead className="text-[10px] uppercase">Timing</TableHead>
                <SortHead label="Start Date" field="start_date" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} />
                <TableHead className="text-[10px] uppercase">Mode</TableHead>
                <TableHead className="text-[10px] uppercase text-center">Students</TableHead>
                <TableHead className="text-[10px] uppercase">Syllabus</TableHead>
                <SortHead label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} />
                <TableHead className="text-[10px] uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map(b => (
                <TableRow key={b.id} className={`hover:bg-violet-50/30 cursor-pointer ${b.is_delayed ? 'bg-red-50/40' : ''}`} onClick={() => setViewBatchId(b.id)} data-testid={`batch-row-${b.id}`}>
                  <TableCell className="text-xs font-medium text-violet-600">{b.batch_number ? `#${b.batch_number}` : '-'}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{b.name || b.course_name}</div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">{b.course_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{b.trainer_name || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{b.centre_name}</TableCell>
                  <TableCell className="text-xs" onClick={(e) => e.stopPropagation()}>
                    <LabAssignCell batch={b} labs={labs} onAssign={handleAssignLab} />
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{b.start_time && b.end_time ? `${b.start_time} - ${b.end_time}` : b.duration_raw || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{b.start_date || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{b.training_mode || b.mode_name || '-'}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="text-[10px]"><Users className="w-3 h-3 mr-0.5" />{b.student_count}</Badge></TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <SyllabusLinkCell batchId={b.id} link={batchLinks[b.id]} syllabi={syllabi} onChange={handleLinkSyllabus} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      <Badge variant="outline" className={`text-[10px] ${bsColors[b.batch_status] || bsColors[b.status] || ''}`}>{b.batch_status || b.status}</Badge>
                      {b.is_delayed && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200"><AlertTriangle className="w-3 h-3 mr-0.5" />{b.delay_days}d late</Badge>}
                      {(b.audit_trainer_name || b.notes_of_auditor) && (
                        <span
                          title={`Audit Trainer: ${b.audit_trainer_name || '—'}${b.notes_of_auditor ? `\nNotes: ${b.notes_of_auditor}` : ''}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 max-w-[180px]"
                          data-testid={`bm-audit-chip-${b.id}`}
                        >
                          <ClipboardCheck className="w-3 h-3 shrink-0" />
                          <span className="truncate">
                            {b.audit_trainer_name ? `Audit · ${b.audit_trainer_name}` : 'Auditor Notes'}
                          </span>
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); setViewBatchId(b.id); }} title="View" data-testid={`view-batch-${b.id}`}>
                      <Eye className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">Page {page} of {totalPages} ({total} batches)</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 rounded-sm"><ChevronLeft className="w-3.5 h-3.5" /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 rounded-sm"><ChevronRight className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteBatch} onOpenChange={() => setDeleteBatch(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Batch?</AlertDialogTitle><AlertDialogDescription>This will permanently delete this batch.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={async () => { try { await api.delete(`/training/batches/${deleteBatch.id}`); toast.success('Deleted'); setDeleteBatch(null); fetchBatches(); } catch { toast.error('Failed'); } }} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SyncBadge({ status, syncing }) {
  if (syncing) return <div className="flex items-center gap-1.5 text-[10px] text-blue-600" data-testid="crm-sync-status"><RefreshCw className="w-3 h-3 animate-spin" /><span>Syncing...</span></div>;
  if (!status) return null;
  const ok = status.status === 'success';
  const t = status.completed_at || status.started_at;
  const label = t ? new Date(t).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-slate-500" data-testid="crm-sync-status">
      {ok ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <AlertCircle className="w-3 h-3 text-red-400" />}
      <span>CRM: {label}</span>
      {ok && <span className="text-slate-400">({status.total_batches} batches)</span>}
    </div>
  );
}

function BatchDetailView({ batchId, onBack }) {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [syncing, setSyncing] = useState(false);
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [studentFilter, setStudentFilter] = useState(null); // null=all, 'has_dues', 'overdue', 'partially_paid', 'write_off', 'upgrade'
  const [labs, setLabs] = useState([]);
  const [savingLab, setSavingLab] = useState(false);

  const loadBatch = useCallback(() => {
    setLoading(true);
    api.get(`/training/batches/${batchId}`).then(r => setBatch(r.data)).catch(() => toast.error('Failed to load batch')).finally(() => setLoading(false));
  }, [batchId]);

  useEffect(() => { loadBatch(); }, [loadBatch]);

  // Lab master — needed for the inline Lab assignment card in Overview.
  // Loaded once when the detail view mounts.
  useEffect(() => {
    api.get('/venue/admin/labs')
      .then(r => setLabs(r.data.labs || []))
      .catch(() => setLabs([]));
  }, []);

  const handleAssignLab = async (labId) => {
    const value = (!labId || labId === '__none__') ? null : labId;
    setSavingLab(true);
    try {
      await api.put(`/training/batches/${batchId}`, { lab_id: value });
      // Refresh batch so lab_name (server-enriched) updates
      const r = await api.get(`/training/batches/${batchId}`);
      setBatch(r.data);
      toast.success(value ? 'Lab assigned' : 'Lab cleared');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update lab');
    } finally {
      setSavingLab(false);
    }
  };

  const handleSyncStudents = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/training/batches/${batchId}/sync-students`);
      toast.success(`Synced: ${r.data.enrollments_created} students (${r.data.deleted_old} old removed, ${r.data.mismatches} unmatched)`);
      loadBatch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    setSyncing(false);
  };

  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '-';

  if (loading) return <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div>;
  if (!batch) return <p className="text-sm text-slate-400 text-center py-12">Batch not found</p>;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'students', label: `Students (${batch.student_count || 0})` },
  ];

  return (
    <BookmarkMapProvider>
    <div className="space-y-6" data-testid="batch-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="rounded-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div>
          <h2 className="text-lg font-bold">{batch.name || batch.course_name}</h2>
          <p className="text-xs text-slate-400">{batch.course_name} &middot; {batch.centre_name} &middot; {batch.trainer_name || 'No trainer'}</p>
        </div>
        <div className="flex-1" />
        {batch.crm_link && (
          <a href={batch.crm_link} target="_blank" rel="noopener noreferrer" data-testid="zoho-crm-link">
            <Button variant="outline" size="sm" className="rounded-sm text-xs border-blue-200 text-blue-700 hover:bg-blue-50">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />View in Zoho CRM
            </Button>
          </a>
        )}
        {batch.zoho_batch_id && (
          <Button variant="outline" size="sm" onClick={handleSyncStudents} disabled={syncing} className="rounded-sm text-xs border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="sync-batch-students">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Refresh Students'}
          </Button>
        )}
        <Badge variant="outline" className={`${bsColors[batch.batch_status] || ''}`}>{batch.batch_status || batch.status}</Badge>
        {batch.is_delayed && <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200"><AlertTriangle className="w-3 h-3 mr-0.5" />Delayed</Badge>}
      </div>

      <div className="border-b flex gap-0">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${tab === t.id ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard label="Start Date" value={batch.start_date} />
            <InfoCard label="End Date" value={batch.end_date} />
            <InfoCard label="Timing" value={batch.start_time && batch.end_time ? `${batch.start_time} - ${batch.end_time}` : batch.duration_raw} />
            <InfoCard label="Batch Type" value={batch.batch_type} />
            <InfoCard label="Training Mode" value={batch.training_mode || batch.mode_name} />
            <InfoCard label="Trainer" value={batch.trainer_name} />
            <InfoCard label="Centre" value={batch.centre_name} />
            <LabInfoCard batch={batch} labs={labs} onAssign={handleAssignLab} saving={savingLab} />
            <InfoCard label="Students" value={batch.student_count} />
            {batch.batch_number && <InfoCard label="Batch Number" value={`#${batch.batch_number}`} />}
            {batch.crm_link && (
              <Card className="p-4 border">
                <p className="text-[10px] text-slate-400 uppercase">Zoho CRM</p>
                <a href={batch.crm_link} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-600 hover:underline flex items-center gap-1 mt-1" data-testid="zoho-crm-overview-link"><ExternalLink className="w-3 h-3" /> Open in CRM</a>
              </Card>
            )}
            {batch.curriculum_url && (
              <Card className="p-4 border">
                <p className="text-[10px] text-slate-400 uppercase">Curriculum</p>
                <a href={batch.curriculum_url} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-600 hover:underline flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> View</a>
              </Card>
            )}
            {batch.whatsapp_link_crm && (
              <Card className="p-4 border" data-testid="bm-wa-crm-card">
                <p className="text-[10px] text-slate-400 uppercase flex items-center gap-1">WhatsApp Group <span className="ml-auto text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">CRM</span></p>
                <a href={batch.whatsapp_link_crm} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-600 hover:underline flex items-center gap-1 mt-1 truncate"><ExternalLink className="w-3 h-3" /> Open link</a>
              </Card>
            )}
            {batch.audit_trainer_name && (
              <Card className="p-4 border" data-testid="bm-audit-trainer-card">
                <p className="text-[10px] text-slate-400 uppercase flex items-center gap-1">Audit Trainer <span className="ml-auto text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">CRM</span></p>
                <p className="text-sm text-slate-800 mt-1">{batch.audit_trainer_name}</p>
              </Card>
            )}
          </div>

          {batch.notes_of_auditor && (
            <Card className="p-4 border" data-testid="bm-auditor-notes-card">
              <p className="text-[10px] text-slate-400 uppercase flex items-center gap-1">Notes of Auditor <span className="ml-2 text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">CRM</span></p>
              <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{batch.notes_of_auditor}</p>
            </Card>
          )}

          {/* Revenue at a Glance */}
          {batch.students && batch.students.length > 0 && <BatchFinancialSummary students={batch.students} fmt={fmt} />}
        </div>
      )}

      {tab === 'students' && <BatchStudentsTab batch={batch} fmt={fmt} studentFilter={studentFilter} setStudentFilter={setStudentFilter} expandedStudentId={expandedStudentId} setExpandedStudentId={setExpandedStudentId} />}
    </div>
    </BookmarkMapProvider>
  );
}

function BatchFinancialSummary({ students, fmt }) {
  const totalAmount = students.reduce((s, st) => s + (st.total_amount || 0), 0);
  const paidAmount = students.reduce((s, st) => s + (st.paid_amount || 0), 0);
  const pendingAmount = students.reduce((s, st) => s + (st.due_amount || 0), 0);
  const writeOffAmount = students.reduce((s, st) => s + (st.write_off_amount || 0), 0);
  const overdueCount = students.filter(st => st.has_overdue).length;
  const partiallyPaidCount = students.filter(st => st.has_partially_paid).length;
  const collectionRate = totalAmount > 0 ? ((paidAmount / totalAmount) * 100).toFixed(1) : 0;

  return (
    <Card className="border p-5" data-testid="batch-financial-summary">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="w-4 h-4 text-violet-500" />
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Revenue at a Glance</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
        <div className="text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Total Fees</p>
          <p className="text-lg font-bold text-slate-800">{fmt(totalAmount)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Collected</p>
          <p className="text-lg font-bold text-emerald-600">{fmt(paidAmount)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Pending</p>
          <p className="text-lg font-bold text-amber-600">{fmt(pendingAmount)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Write-Off</p>
          <p className={`text-lg font-bold ${writeOffAmount > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{fmt(writeOffAmount)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Overdue</p>
          <p className={`text-lg font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-slate-400'}`}>{overdueCount} <span className="text-xs font-normal text-slate-400">students</span></p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Partially Paid</p>
          <p className={`text-lg font-bold ${partiallyPaidCount > 0 ? 'text-orange-600' : 'text-slate-400'}`}>{partiallyPaidCount} <span className="text-xs font-normal text-slate-400">students</span></p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Collection Rate</p>
          <p className={`text-lg font-bold ${collectionRate >= 80 ? 'text-emerald-600' : collectionRate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{collectionRate}%</p>
        </div>
      </div>
    </Card>
  );
}

function BatchStudentsTab({ batch, fmt, studentFilter, setStudentFilter, expandedStudentId, setExpandedStudentId }) {
  const allStudents = batch.students || [];

  // Compute KPI values from all students (unfiltered)
  const kpi = {
    total: allStudents.length,
    totalAmount: allStudents.reduce((s, st) => s + (st.total_amount || 0), 0),
    paidAmount: allStudents.reduce((s, st) => s + (st.paid_amount || 0), 0),
    pendingAmount: allStudents.reduce((s, st) => s + (st.due_amount || 0), 0),
    overdueCount: allStudents.filter(st => st.has_overdue).length,
    partiallyPaidCount: allStudents.filter(st => st.has_partially_paid).length,
    writeOffAmount: allStudents.reduce((s, st) => s + (st.write_off_amount || 0), 0),
    writeOffCount: allStudents.filter(st => (st.write_off_amount || 0) > 0).length,
    upgradeCount: allStudents.filter(st => st.enrollment_type === 'upgrade').length,
  };

  // Apply filter
  const filtered = studentFilter ? allStudents.filter(s => {
    if (studentFilter === 'has_dues') return (s.due_amount || 0) > 0;
    if (studentFilter === 'overdue') return s.has_overdue;
    if (studentFilter === 'partially_paid') return s.has_partially_paid;
    if (studentFilter === 'write_off') return (s.write_off_amount || 0) > 0;
    if (studentFilter === 'upgrade') return s.enrollment_type === 'upgrade';
    return true;
  }) : allStudents;

  const toggleFilter = (key) => setStudentFilter(studentFilter === key ? null : key);

  const cards = [
    { key: null, label: 'Total Students', value: kpi.total, isCurrency: false, color: 'text-slate-800' },
    { key: null, label: 'Total Amount', value: kpi.totalAmount, isCurrency: true, color: 'text-slate-800' },
    { key: null, label: 'Paid Amount', value: kpi.paidAmount, isCurrency: true, color: 'text-emerald-600' },
    { key: 'has_dues', label: 'Pending Amount', value: kpi.pendingAmount, isCurrency: true, color: 'text-amber-600', count: allStudents.filter(st => (st.due_amount || 0) > 0).length, activeBg: 'bg-amber-50 border-amber-400 ring-1 ring-amber-200', hover: 'hover:border-amber-300' },
    { key: 'overdue', label: 'Overdue', value: kpi.overdueCount, isCurrency: false, color: 'text-red-600', activeBg: 'bg-red-50 border-red-400 ring-1 ring-red-200', hover: 'hover:border-red-300' },
    { key: 'partially_paid', label: 'Partially Paid', value: kpi.partiallyPaidCount, isCurrency: false, color: 'text-orange-600', activeBg: 'bg-orange-50 border-orange-400 ring-1 ring-orange-200', hover: 'hover:border-orange-300' },
    { key: 'write_off', label: 'Write-Off', value: kpi.writeOffAmount, isCurrency: true, color: 'text-rose-600', count: kpi.writeOffCount, activeBg: 'bg-rose-50 border-rose-400 ring-1 ring-rose-200', hover: 'hover:border-rose-300' },
    { key: 'upgrade', label: 'Upgrade/Revision', value: kpi.upgradeCount, isCurrency: false, color: 'text-purple-600', activeBg: 'bg-purple-50 border-purple-400 ring-1 ring-purple-200', hover: 'hover:border-purple-300' },
  ];

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="batch-student-kpi-cards">
        {cards.map(c => {
          const isClickable = !!c.key;
          const active = studentFilter === c.key;
          return (
            <Card
              key={c.label}
              className={`border p-3 text-center transition-colors ${isClickable ? `cursor-pointer ${active ? c.activeBg : c.hover}` : ''}`}
              onClick={isClickable ? () => toggleFilter(c.key) : undefined}
              data-testid={`batch-student-kpi-${(c.key || c.label).toLowerCase().replace(/[\s/]/g, '-')}`}
            >
              <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold truncate">{c.label}</p>
              <p className={`text-base font-bold ${c.color}`}>
                {c.isCurrency ? fmt(c.value) : c.value}
              </p>
              {c.count != null && <p className="text-[9px] text-slate-400">{c.count} student{c.count !== 1 ? 's' : ''}</p>}
            </Card>
          );
        })}
      </div>

      {/* Active filter indicator */}
      {studentFilter && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
            Filtered: {studentFilter.replace(/_/g, ' ')} ({filtered.length} student{filtered.length !== 1 ? 's' : ''})
          </Badge>
          <button onClick={() => setStudentFilter(null)} className="text-[10px] text-slate-400 hover:text-red-500 underline">Clear filter</button>
        </div>
      )}

      {/* Student Table */}
      <Card className="border p-0">
        {batch.batch_number && (
          <div className="px-4 py-2.5 border-b bg-slate-50/50 flex items-center gap-2">
            <span className="text-[10px] uppercase text-slate-400 font-semibold">Batch #{batch.batch_number}</span>
            <span className="text-[10px] text-slate-400">&middot;</span>
            <span className="text-[10px] text-slate-500">{filtered.length} student{filtered.length !== 1 ? 's' : ''}{studentFilter ? ' (filtered)' : ''}</span>
          </div>
        )}
        {filtered.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8">{studentFilter ? 'No students match this filter.' : 'No students enrolled in this batch.'}</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[10px] uppercase w-8"></TableHead>
              <TableHead className="text-[10px] uppercase">Enrollment No</TableHead>
              <TableHead className="text-[10px] uppercase">Name</TableHead>
              <TableHead className="text-[10px] uppercase">Phone</TableHead>
              <TableHead className="text-[10px] uppercase">Email</TableHead>
              <TableHead className="text-[10px] uppercase">Course</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Due</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(s => (
                <BatchStudentRow
                  key={s.id + (s.enrollment_id || '')}
                  student={s}
                  expanded={expandedStudentId === s.id}
                  onToggle={() => setExpandedStudentId(expandedStudentId === s.id ? null : s.id)}
                  fmt={fmt}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function BatchStudentRow({ student: s, expanded, onToggle, fmt }) {
  const { map } = useBookmarkMap();
  const bmHighlight = bookmarkRowClass(s.enrollment_no, map);
  return (
    <>
      <TableRow className={`hover:bg-violet-50/30 cursor-pointer ${bmHighlight}`} onClick={onToggle} data-testid={`batch-student-row-${s.id}`}>
        <TableCell className="text-center">{expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
        <TableCell className="text-xs"><EnrollmentNoCrmLink enrollmentNo={s.enrollment_no} /></TableCell>
        <TableCell>
          <div className="flex items-center">
            <span className="text-sm font-medium">{s.name}</span>
            <BookmarkInline student={s} />
          </div>
        </TableCell>
        <TableCell className="text-xs text-slate-500">{s.phone || '-'}</TableCell>
        <TableCell className="text-xs text-slate-500">{s.email || '-'}</TableCell>
        <TableCell className="text-xs text-slate-500">{s.course || '-'}</TableCell>
        <TableCell className="text-xs text-right">
          {s.due_amount > 0 ? <span className="text-red-500 font-medium">{fmt(s.due_amount)}</span> : <span className="text-slate-400">{fmt(0)}</span>}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-slate-50/50 p-4">
            <BatchStudentDetail student={s} fmt={fmt} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function BatchStudentDetail({ student: s, fmt }) {
  const [invoices, setInvoices] = useState(null);
  const [attendance, setAttendance] = useState(null);
  useEffect(() => {
    api.get(`/training/students/${s.id}/invoices`).then(r => setInvoices(r.data.invoices || [])).catch(() => setInvoices([]));
    api.get(`/training/students/${s.id}/attendance`).then(r => setAttendance(r.data.attendance || [])).catch(() => setAttendance([]));
  }, [s.id]);

  const presentCount = attendance ? attendance.filter(a => a.status === 'present').length : 0;
  const totalSessions = attendance ? attendance.length : 0;
  const attendanceRate = totalSessions > 0 ? Math.round((presentCount / totalSessions) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Student Details</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            <span className="text-slate-400">Enrollment No:</span>
            <span className="flex items-center gap-2 flex-wrap">
              <ZohoCustomerLink enrollmentNo={s.enrollment_no} zohoCustomerId={s.zoho_customer_id} className="text-xs" />
              <ZohoCrmLeadLink enrollmentNo={s.enrollment_no} compact />
            </span>
            <span className="text-slate-400">Phone:</span><span>{s.phone || '-'}</span>
            <span className="text-slate-400">Email:</span><span>{s.email || '-'}</span>
            <span className="text-slate-400">Course:</span><span>{s.course || '-'}</span>
            <span className="text-slate-400">Branch:</span><span>{s.branch || '-'}</span>
            <span className="text-slate-400">Counsellor:</span><span>{s.salesperson || s.counsellor_name || '-'}</span>
            <span className="text-slate-400">Type:</span><span>{s.enrollment_type === 'upgrade' ? <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200 w-fit">Upgrade/Revision</Badge> : <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 w-fit">Regular</Badge>}</span>
            {s.source === 'zoho' && <><span className="text-slate-400">Source:</span><Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-600 border-blue-200 w-fit">Zoho Books</Badge></>}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Financial Summary</p>
          {(s.total_amount != null && s.total_amount > 0) ? (
            <div className="grid grid-cols-4 gap-2 text-center p-2.5 bg-white border rounded-sm">
              <div><p className="text-xs text-slate-500">Total</p><p className="text-sm font-semibold">{fmt(s.total_amount)}</p></div>
              <div><p className="text-xs text-slate-500">Paid</p><p className="text-sm font-semibold text-emerald-600">{fmt(s.paid_amount)}</p></div>
              <div><p className="text-xs text-slate-500">Write Off</p><p className={`text-sm font-semibold ${s.write_off_amount > 0 ? 'text-red-500' : 'text-slate-400'}`}>{s.write_off_amount > 0 ? fmt(s.write_off_amount) : '-'}</p></div>
              <div><p className="text-xs text-slate-500">Due</p><p className="text-sm font-semibold text-red-500">{fmt(s.due_amount)}</p></div>
            </div>
          ) : (
            <p className="text-xs text-slate-400">No financial data available</p>
          )}
          {invoices && invoices.length > 0 && (
            <div className="mt-3 p-2.5 bg-white border rounded-sm">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Invoices ({invoices.length})</p>
              <div className="space-y-1.5">
                {invoices.map((inv, i) => (
                  <div key={inv.invoice_id || i} className="flex items-center justify-between text-xs border-b border-dashed last:border-0 pb-1.5 last:pb-0">
                    <div className="flex items-center gap-2">
                      <ZohoInvoiceLink invoiceNumber={inv.invoice_number || inv.invoice_id} invoiceId={inv.invoice_id} className="text-xs" />
                      <span className="text-slate-400">{inv.date || ''}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-500">{fmt(inv.total)}</span>
                      <span className="text-emerald-600">{fmt(inv.payment_made)}</span>
                      <span className={`font-medium ${inv.balance > 0 ? 'text-red-600' : 'text-slate-400'}`}>{fmt(inv.balance)}</span>
                      <Badge variant="outline" className={`text-[9px] ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700' : inv.status === 'overdue' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-500'}`}>{inv.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Attendance History */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Attendance History</p>
        {attendance === null ? (
          <p className="text-xs text-slate-400">Loading...</p>
        ) : attendance.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No attendance records found</p>
        ) : (
          <div className="bg-white border rounded-sm overflow-hidden">
            <div className="px-3 py-2 border-b bg-slate-50/50 flex items-center gap-4">
              <span className="text-[10px] text-slate-500">Sessions: <span className="font-semibold text-slate-700">{totalSessions}</span></span>
              <span className="text-[10px] text-slate-500">Present: <span className="font-semibold text-emerald-600">{presentCount}</span></span>
              <span className="text-[10px] text-slate-500">Rate: <span className={`font-semibold ${attendanceRate >= 75 ? 'text-emerald-600' : attendanceRate >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{attendanceRate}%</span></span>
              {totalSessions <= 3 && <Badge variant="outline" className="text-[8px] bg-amber-50 text-amber-700 border-amber-200">Low data</Badge>}
            </div>
            <div className="max-h-[200px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/30">
                    <TableHead className="text-[9px] uppercase">Date</TableHead>
                    <TableHead className="text-[9px] uppercase">Status</TableHead>
                    <TableHead className="text-[9px] uppercase">Mode</TableHead>
                    <TableHead className="text-[9px] uppercase">Trainer</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendance.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{a.date ? new Date(a.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[9px] ${a.status === 'present' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{a.status || '-'}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{a.mode_of_training || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-500">{a.trainer_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <Card className="p-4 border">
      <p className="text-[10px] text-slate-400 uppercase">{label}</p>
      <p className="text-sm font-semibold mt-1">{value || '-'}</p>
    </Card>
  );
}
